"use client";

import { useState, useCallback } from "react";
import { cn } from "@/app/lib/utils/cn";
import { FlockWord } from "@/app/ui/molecular/typography";
import FadeIn from "@/app/ui/atomic/animations/FadeIn";
import PillLink from "@/app/ui/atomic/content/PillLink";

interface Step {
  title: string;
  shortTitle: string;
  timeline: string;
  description: React.ReactNode;
}

const steps: Step[] = [
  {
    title: "Submit Your Proposal",
    shortTitle: "Submit",
    timeline: "Day 1",
    description: (
      <>
        Pay a one-time $500 submission fee and share your business concept or growth plan. We&apos;ll 
        review your proposal and provide detailed feedback within 2 weeks.
      </>
    ),
  },
  {
    title: "Expert Review",
    shortTitle: "Review",
    timeline: "Week 1-2",
    description: (
      <>
        Our team evaluates your business plan or growth strategy, market opportunity, and organizational 
        structure. We&apos;ll work with you to refine your proposal for success.
      </>
    ),
  },
  {
    title: "Go Live",
    shortTitle: "Live",
    timeline: "Week 3-4",
    description: (
      <>
        Once approved, your <FlockWord word="Tank" small /> campaign page goes 
        live. Invite your community and network to invest in your organization.
      </>
    ),
  },
  {
    title: "Get Funded",
    shortTitle: "Funded",
    timeline: "Month 3-4+",
    description: (
      <>
        When your campaign reaches its goal, receive your funding to launch your 
        organization or fuel its growth. We&apos;ll support you every step of the way.
      </>
    ),
  },
];

export default function HorizontalSteps() {
  const [activeStep, setActiveStep] = useState<number>(0);

  const handleStepClick = useCallback((index: number) => {
    setActiveStep(index);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 xl:px-10 py-6">
      <FadeIn>
        <div className="bg-gradient-purple py-3 rounded-xl mb-6">
          <h3 className="font-['Font-Bold'] text-xl md:text-2xl text-center">
            <span className="text-(--gold)">4 Steps</span>{" "}
            <span className="text-(--brand)">to Launch or Grow</span>
          </h3>
        </div>
      </FadeIn>

      <FadeIn delay={100}>
        <div className="relative">
          <div className="absolute top-6 left-[10%] right-[10%] h-1 bg-(--border-color) rounded-full hidden sm:block" />
          <div 
            className="absolute top-6 left-[10%] h-1 bg-gradient-to-r from-(--gold) to-(--brand) rounded-full hidden sm:block transition-all duration-500"
            style={{ width: `${(activeStep / 3) * 80}%` }}
          />

          <div className="flex justify-between items-start relative">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                className={cn(
                  "flex flex-col items-center gap-2 group cursor-pointer transition-all duration-300 flex-1",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--purple) focus-visible:ring-offset-2 rounded-lg p-1"
                )}
                aria-label={`Step ${index + 1}: ${step.title}`}
                aria-pressed={activeStep === index}
              >
                <div
                  className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 relative z-10",
                    activeStep === index
                      ? "bg-(--gold) scale-110 shadow-lg"
                      : activeStep > index
                        ? "bg-(--brand)"
                        : "bg-(--bg-card) border-2 border-(--border-color-strong)"
                  )}
                >
                  <span 
                    className={cn(
                      "text-lg font-['Font-Bold']",
                      activeStep === index
                        ? "text-(--text-on-gold)"
                        : activeStep > index
                          ? "text-white"
                          : "text-(--text-muted)"
                    )}
                  >
                    {index + 1}
                  </span>
                </div>
                <span
                  className={cn(
                    "text-xs sm:text-sm font-['Font-Bold'] text-center transition-colors duration-300",
                    activeStep === index ? "text-(--gold-text)" : "text-(--text-muted)"
                  )}
                >
                  <span className="hidden sm:inline">{step.shortTitle}</span>
                  <span className="sm:hidden">{index + 1}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={200}>
        <div className="mt-6 theme-card-alt rounded-xl p-5 shadow-md border-l-4 border-(--gold-accent) min-h-[120px]">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-(--gold) items-center justify-center shrink-0">
              <span className="text-lg font-['Font-Bold'] text-(--text-on-gold)">
                {activeStep + 1}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h4 className="font-['Font-Bold'] text-(--text-headline) text-lg">
                  {steps[activeStep]!.title}
                </h4>
                <span className="text-xs font-['Font-Bold'] text-(--brand) bg-(--purple) px-2 py-1 rounded">
                  {steps[activeStep]!.timeline}
                </span>
              </div>
              <p className="text-(--text-muted) text-sm leading-relaxed">
                {steps[activeStep]!.description}
              </p>
            </div>
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={300}>
        <div className="mt-4 p-4 bg-(--bg-secondary) rounded-lg">
          <p className="text-(--text-muted) text-sm text-center">
            <span className="font-['Font-Bold'] text-(--text-secondary)">Typical timeline:</span>{" "}
            3-4 months from submission to funded launch or growth milestone.{" "}
            <span className="italic text-xs">Some campaigns may take longer depending on funding goals—that&apos;s okay!</span>
          </p>
        </div>
      </FadeIn>

      <FadeIn delay={400}>
        <div className="mt-4 text-center">
          <p className="text-(--text-muted) text-sm">
            Ready to start your journey?{" "}
            <PillLink href="/connect/contact">Contact us</PillLink>{" "}
            to learn more.
          </p>
        </div>
      </FadeIn>
    </div>
  );
}
